import { useState, useEffect } from "react";
import { Plus, Trash2, Shield, User, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { staffLineIdsApi } from "@/lib/unifiedApi";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { name: "", line_user_id: "", is_admin: false };

export default function StaffLinePage() {
  const { isAdmin } = useAuth();
  const [staff, setStaff]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm]     = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    const unsub = staffLineIdsApi.subscribeAll((data) => {
      setStaff(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  async function save() {
    if (!form.name.trim() || !form.line_user_id.trim()) {
      toast.error("กรุณากรอกชื่อและ LINE User ID");
      return;
    }
    setSaving(true);
    try {
      await staffLineIdsApi.create({ name: form.name.trim(), line_user_id: form.line_user_id.trim(), is_admin: form.is_admin });
      toast.success("เพิ่ม staff สำเร็จ");
      setForm(EMPTY);
      setAddOpen(false);
    } catch (e) {
      toast.error("เกิดข้อผิดพลาด: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove(id, name) {
    if (!confirm(`ลบ "${name}" ออกจากระบบ?`)) return;
    try {
      await staffLineIdsApi.delete(id);
      toast.success("ลบแล้ว");
    } catch {
      toast.error("เกิดข้อผิดพลาด");
    }
  }

  if (!isAdmin()) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center" style={{ backgroundColor: "#0a0a0a" }}>
        <p className="text-gray-500">ต้องเป็น Admin เท่านั้น</p>
      </div>
    );
  }

  return (
    <div className="pt-16 lg:pt-6 px-4 sm:px-6 pb-6 space-y-6 min-h-screen" style={{ backgroundColor: "#0a0a0a", color: "#fff" }}>
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield size={24} className="text-[#BFFF00]" />
            Staff LINE IDs
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            ลงทะเบียน LINE User ID ของ staff — bot จะแยก staff vs ลูกค้าอัตโนมัติ
          </p>
        </div>
        <Button onClick={() => setAddOpen(true)} className="bg-[#BFFF00] text-black hover:bg-[#a8e000] font-semibold gap-2">
          <Plus size={16} /> เพิ่ม Staff
        </Button>
      </div>

      {/* How to get Line User ID */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-sm text-blue-300 space-y-1.5">
        <p className="font-semibold text-blue-200 flex items-center gap-2"><AlertCircle size={14}/> วิธีหา LINE User ID</p>
        <p>1. เปิด LINE Developers Console → channel ของ bot</p>
        <p>2. ใน tab "Messaging API" → เลื่อนหา "Your user ID" (ขึ้นต้นด้วย U)</p>
        <p className="text-gray-400">หรือ: ให้ staff ส่งข้อความมาที่ bot แล้วดู log ใน Vercel → Functions</p>
      </div>

      {/* Staff list */}
      <div className="bg-[#1a1a1a] rounded-2xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-500">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-[#BFFF00] mr-3" />
            กำลังโหลด...
          </div>
        ) : staff.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
            <User size={32} className="mb-3 opacity-40" />
            <p>ยังไม่มี staff ที่ลงทะเบียน</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5">
                {["ชื่อ", "LINE User ID", "สิทธิ์", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staff.map((s) => (
                <tr key={s.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3 text-white font-medium">{s.name}</td>
                  <td className="px-4 py-3 font-mono text-gray-400 text-xs">{s.line_user_id}</td>
                  <td className="px-4 py-3">
                    {s.is_admin
                      ? <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#BFFF00]/20 text-[#BFFF00] border border-[#BFFF00]/30">Admin</span>
                      : <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-white/10 text-gray-300 border border-white/10">Staff</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => remove(s.id, s.name)} className="p-1.5 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add dialog */}
      <Dialog open={addOpen} onOpenChange={(o) => !o && setAddOpen(false)}>
        <DialogContent className="bg-[#1a1a1a] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>เพิ่ม Staff</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">ชื่อ *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="ชื่อ-นามสกุล หรือชื่อเล่น" className="bg-white/5 border-white/10 text-white placeholder:text-gray-600" />
            </div>
            <div>
              <Label className="text-gray-400 text-xs mb-1 block">LINE User ID *</Label>
              <Input value={form.line_user_id} onChange={(e) => setForm({ ...form, line_user_id: e.target.value })} placeholder="Uxxxxxxxxx..." className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 font-mono" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" id="is_admin" checked={form.is_admin} onChange={(e) => setForm({ ...form, is_admin: e.target.checked })} className="rounded" />
              <Label htmlFor="is_admin" className="text-gray-300 text-sm cursor-pointer">
                เป็น Admin (รับ daily summary + ยืนยันรายการได้)
              </Label>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => setAddOpen(false)} variant="outline" className="flex-1 border-white/10 text-white bg-transparent hover:bg-white/5">ยกเลิก</Button>
              <Button onClick={save} disabled={saving} className="flex-1 bg-[#BFFF00] text-black hover:bg-[#a8e000] font-semibold">
                {saving ? "กำลังบันทึก..." : "เพิ่ม"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
